import { Vector3 } from 'three';
import { BALANCE } from '../config/balance';
import type { Drone } from '../enemies/Drone';

const scratchToDrone = new Vector3();

export type NetOutcome = 'caught' | 'cooldown' | 'out-of-range' | 'no-target';

/**
 * A short-range capture net. Only one drone is caught per shot; the net
 * immobilises and disarms it for a few seconds so it can be finished off.
 */
export class NetGun {
  private cooldown = 0;
  private lastOutcomeValue: NetOutcome = 'no-target';

  get lastOutcome(): NetOutcome {
    return this.lastOutcomeValue;
  }

  get cooldownRemaining(): number {
    return this.cooldown;
  }

  /** 1 just after a shot, 0 when the net is fully charged again. */
  get cooldownRatio(): number {
    return this.cooldown > 0 ? this.cooldown / BALANCE.tools.netCooldown : 0;
  }

  update(dt: number): void {
    this.cooldown = Math.max(0, this.cooldown - dt);
  }

  reset(): void {
    this.cooldown = 0;
    this.lastOutcomeValue = 'no-target';
  }

  /**
   * Fire the net down the aim. Returns the drone that was caught, or null when
   * nothing was in range or the gun is cooling down. `lastOutcome` explains a
   * miss so the HUD can tell the player what happened.
   */
  tryFire(origin: Vector3, direction: Vector3, drones: readonly Drone[]): Drone | null {
    if (this.cooldown > 0) {
      this.lastOutcomeValue = 'cooldown';
      return null;
    }

    const cosCone = Math.cos(BALANCE.tools.netCone);
    let best: Drone | null = null;
    let bestDistance = Infinity;
    let closestInCone = Infinity;
    for (const drone of drones) {
      if (drone.isDestroyed) continue;
      scratchToDrone.subVectors(drone.position, origin);
      const distance = scratchToDrone.length();
      if (distance < 1e-4) continue;
      scratchToDrone.divideScalar(distance);
      if (scratchToDrone.dot(direction) < cosCone) continue;
      closestInCone = Math.min(closestInCone, distance);
      if (distance > BALANCE.tools.netRange) continue;
      if (distance < bestDistance) {
        best = drone;
        bestDistance = distance;
      }
    }

    if (!best) {
      this.lastOutcomeValue = closestInCone < Infinity ? 'out-of-range' : 'no-target';
      return null;
    }
    this.cooldown = BALANCE.tools.netCooldown;
    best.net(BALANCE.tools.netDuration);
    this.lastOutcomeValue = 'caught';
    return best;
  }
}
